import React, { useEffect, useState } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Link } from '../../types/Link';
import { dataService } from '../../services/DataService';
import LinkForm from './LinkForm';

interface ModuleLinksProps {
  moduleId: string;
}

export default function ModuleLinks({ moduleId }: ModuleLinksProps) {
  const [links, setLinks] = useState<Link[]>([]);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadLinks = async () => {
    setIsLoading(true);
    const res = await dataService.getItems(`modules/${moduleId}/links`);
    if (res.statusCode === 200) {
      const linksData = res.body.map((link: any) => ({
        ...link,
        id: link.id || link.docId || link.documentId,
      }));
      setLinks(linksData);
    }
    setIsLoading(false);
  };

  const handleSave = async (link: Link) => {
    try {
      if (isEditing && isEditing !== 'new') {
        await dataService.updateItem(`modules/${moduleId}/links`, link.id, link);
      } else {
        await dataService.setItem(`modules/${moduleId}/links`, undefined, {
          ...link,
          moduleId,
          order: links.length,
        });
      }
      setIsEditing(null);
      loadLinks();
    } catch (error) {
      console.error('Error saving link:', error);
    }
  };

  const handleDelete = async (linkId: string) => {
    if (window.confirm('Are you sure you want to delete this link?')) {
      try {
        await dataService.deleteItem(`modules/${moduleId}/links`, linkId);
        loadLinks();
      } catch (error) {
        console.error('Error deleting link:', error);
      }
    }
  };

  const getEmptyLink = (): Link => ({
    id: '',
    moduleId,
    order: links.length,
    url: '',
    url_fr: '',
    url_it: '',
    title: '',
    title_fr: '',
    title_it: '',
  });

  const reorderLinks = async (items: Link[]) => {
    try {
      const reorderData = {
        collection: `modules/${moduleId}/links`,
        items: items.map((link, index) => ({
          id: link.id,
          order: index,
        })),
      };

      await dataService.reorderItems(reorderData);
      loadLinks();
    } catch (error) {
      console.error('Error reordering links:', error);
    }
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(links);
    const [reorderedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, reorderedItem);

    setLinks(reorderedItems);
    reorderLinks(reorderedItems);
  };

  useEffect(() => {
    loadLinks();
  }, [moduleId]);

  if (isLoading) {
    return <div>Loading links...</div>;
  }

  return (
    <Row>
      <Col>
        {isEditing ? (
          <LinkForm
            initialValues={
              isEditing === 'new'
                ? getEmptyLink()
                : links.find((l) => l.id === isEditing) || getEmptyLink()
            }
            onSave={handleSave}
            onCancel={() => setIsEditing(null)}
          />
        ) : (
          <>
            <div className="mb-3">
              <Button onClick={() => setIsEditing('new')}>Add New Link</Button>
            </div>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="links">
                {(provided) => (
                  <ul
                    className="item-list no-list-style"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {links
                      .sort((a, b) => a.order - b.order)
                      .map((link, index) => (
                        <Draggable
                          key={link.id}
                          draggableId={link.id}
                          index={index}
                        >
                          {(dragProvided) => (
                            <li
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              className="list-item p-3 mb-2 border rounded"
                            >
                              <div className="d-flex align-items-center">
                                <span className="me-3">⋮⋮</span>
                                <div className="flex-grow-1">
                                  <div>
                                    <div className="mb-2">
                                      DE:
                                      {' '}
                                      <a
                                        href={link.url}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        {link.title}
                                      </a>
                                    </div>
                                    <div className="mb-2">
                                      FR:
                                      {' '}
                                      <a
                                        href={link.url_fr}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        {link.title_fr}
                                      </a>
                                    </div>
                                    <div className="mb-2">
                                      IT:
                                      {' '}
                                      <a
                                        href={link.url_it}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        {link.title_it}
                                      </a>
                                    </div>
                                  </div>
                                  <div className="mt-3">
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      className="me-2"
                                      onClick={() => link.id && setIsEditing(link.id)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => link.id && handleDelete(link.id)}
                                    >
                                      Delete
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </li>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </DragDropContext>
          </>
        )}
      </Col>
    </Row>
  );
}
